import { verificationHandoffUrl } from '@overlayqa/shared';

/**
 * Pure decision behind the dashboard "Verify fix" action.
 *
 * Routing:
 *  - no pageUrl                    -> `no-page-url`
 *  - PUBLIC http/https             -> `server-verify` — the server re-captures the
 *                                     page itself (Puppeteer) and runs the check;
 *                                     the extension is NOT required.
 *  - local / private + extension   -> `open-handoff` — the dashboard can't reach a
 *                                     localhost/private page, and the server SSRF
 *                                     blocklist would reject it, so the extension
 *                                     (running on the page) re-captures via the
 *                                     `#oq-verify-issue=<id>` fragment.
 *  - local / private + no extension-> `extension-required` — prompt an install
 *                                     instead of opening a dead tab.
 *
 * Kept pure (no DOM, no window) so it is trivially unit-testable; the caller
 * owns extension detection (useExtensionDetection), the server API call, and the
 * UI side effects.
 */
export type DashboardVerifyHandoff =
  | { kind: 'no-page-url' }
  | { kind: 'server-verify' }
  | { kind: 'extension-required' }
  | { kind: 'open-handoff'; url: string };

/** Hostnames the server cannot reach (and its SSRF blocklist would reject). */
const PRIVATE_HOST_PATTERNS: RegExp[] = [
  /^localhost$/i,
  /^127\./, // 127.0.0.0/8 loopback
  /^0\.0\.0\.0$/,
  /^\[?::1\]?$/, // IPv6 loopback
  /\.local$/i, // mDNS / Bonjour
  /^10\./, // 10.0.0.0/8
  /^192\.168\./, // 192.168.0.0/16
  /^172\.(1[6-9]|2\d|3[0-1])\./, // 172.16.0.0/12
];

/**
 * True for an http/https URL whose host is publicly reachable (not localhost,
 * loopback, an RFC-1918 private range, or an mDNS `.local` name). Non-http
 * schemes (file:, chrome:, etc.) are never public.
 */
export function isPublicHttpUrl(pageUrl: string): boolean {
  let u: URL;
  try {
    u = new URL(pageUrl);
  } catch {
    return false;
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
  return !PRIVATE_HOST_PATTERNS.some((re) => re.test(u.hostname));
}

export function resolveDashboardVerifyHandoff(opts: {
  pageUrl: string | null | undefined;
  issueId: string;
  extensionDetected: boolean;
}): DashboardVerifyHandoff {
  const { pageUrl, issueId, extensionDetected } = opts;

  // Missing page URL is unrecoverable regardless of route — it must win.
  if (!pageUrl) return { kind: 'no-page-url' };

  // Public pages are verified server-side; no extension needed.
  if (isPublicHttpUrl(pageUrl)) return { kind: 'server-verify' };

  // Local / private pages require the extension to re-capture the live page.
  if (!extensionDetected) return { kind: 'extension-required' };
  return { kind: 'open-handoff', url: verificationHandoffUrl(pageUrl, 'issue', issueId) };
}
